import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Badge,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  CheckCircle as ResolveIcon,
  Cancel as CancelIcon,
  Map as MapIcon,
  Warning as EmergencyIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { emergencyService } from '@/services/emergencyService';

interface EmergencyAlert {
  id: number;
  user: {
    id: number;
    name: string;
    email: string;
    phone: string;
  };
  type: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'active' | 'resolved' | 'cancelled';
  location: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  description?: string;
  responseTime?: number;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

const EmergencyAlerts: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    type: 'all',
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEmergency, setSelectedEmergency] = useState<EmergencyAlert | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['emergencies', page, rowsPerPage, searchTerm, filters],
    queryFn: () => emergencyService.getEmergencies({
      page: page + 1,
      limit: rowsPerPage,
      search: searchTerm,
      ...filters,
    }),
  });

  const resolveMutation = useMutation({
    mutationFn: (id: number) => emergencyService.resolveEmergency(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emergencies'] });
      handleMenuClose();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => emergencyService.cancelEmergency(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emergencies'] });
      handleMenuClose();
    },
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, emergency: EmergencyAlert) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmergency(emergency);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEmergency(null);
  };

  const handleViewDetails = () => {
    if (selectedEmergency) {
      setDetailsOpen(true);
    }
    handleMenuClose();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'error';
      case 'resolved':
        return 'success';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatEmergencyType = (type: string) => {
    const types: Record<string, string> = {
      heart_attack: 'Infarto',
      stroke: 'ACV',
      fall: 'Caída',
      general: 'General',
    };
    return types[type] || type;
  };

  const activeCount = data?.stats?.active || 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Alertas de Emergencia
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona y monitorea las alertas de emergencia
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<MapIcon />}
            onClick={() => navigate('/emergencies/map')}
          >
            Ver Mapa
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Exportar
          </Button>
          <IconButton onClick={() => refetch()}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar por nombre, email o ubicación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterOpen(!filterOpen)}
            >
              Filtros
            </Button>
            <Badge badgeContent={activeCount} color="error">
              <Chip
                icon={<EmergencyIcon />}
                label="Activas"
                color="error"
                variant={filters.status === 'active' ? 'filled' : 'outlined'}
                onClick={() => setFilters({ ...filters, status: filters.status === 'active' ? 'all' : 'active' })}
              />
            </Badge>
          </Box>

          {filterOpen && (
            <Box display="flex" gap={2} mt={2}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.status}
                  label="Estado"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Activas</MenuItem>
                  <MenuItem value="resolved">Resueltas</MenuItem>
                  <MenuItem value="cancelled">Canceladas</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Prioridad</InputLabel>
                <Select
                  value={filters.priority}
                  label="Prioridad"
                  onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  <MenuItem value="critical">Crítica</MenuItem>
                  <MenuItem value="high">Alta</MenuItem>
                  <MenuItem value="medium">Media</MenuItem>
                  <MenuItem value="low">Baja</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filters.type}
                  label="Tipo"
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="heart_attack">Infarto</MenuItem>
                  <MenuItem value="stroke">ACV</MenuItem>
                  <MenuItem value="fall">Caída</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Usuario</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Prioridad</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Ubicación</TableCell>
              <TableCell>Tiempo</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton height={53} />
                  </TableCell>
                </TableRow>
              ))
            ) : data?.emergencies?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No se encontraron emergencias
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data?.emergencies?.map((emergency: EmergencyAlert) => (
                <TableRow key={emergency.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {emergency.user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {emergency.user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={formatEmergencyType(emergency.type)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={emergency.priority}
                      color={getPriorityColor(emergency.priority)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={emergency.status}
                      color={getStatusColor(emergency.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {emergency.location.address || 'Sin dirección'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {formatDistanceToNow(new Date(emergency.createdAt), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, emergency)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
        />
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <ViewIcon sx={{ mr: 1, fontSize: 20 }} /> Ver detalles
        </MenuItem>
        {selectedEmergency?.status === 'active' && (
          <>
            <MenuItem
              onClick={() => selectedEmergency && resolveMutation.mutate(selectedEmergency.id)}
            >
              <ResolveIcon sx={{ mr: 1, fontSize: 20 }} /> Marcar como resuelta
            </MenuItem>
            <MenuItem
              onClick={() => selectedEmergency && cancelMutation.mutate(selectedEmergency.id)}
            >
              <CancelIcon sx={{ mr: 1, fontSize: 20 }} /> Cancelar
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedEmergency && (
          <>
            <DialogTitle>
              Detalles de Emergencia #{selectedEmergency.id}
            </DialogTitle>
            <DialogContent>
              <Box py={2}>
                <Typography variant="subtitle2" color="text.secondary">Usuario</Typography>
                <Typography variant="body1" mb={2}>
                  {selectedEmergency.user.name} ({selectedEmergency.user.email})
                </Typography>

                <Typography variant="subtitle2" color="text.secondary">Tipo</Typography>
                <Chip
                  label={formatEmergencyType(selectedEmergency.type)}
                  color="primary"
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" color="text.secondary">Prioridad</Typography>
                <Chip
                  label={selectedEmergency.priority}
                  color={getPriorityColor(selectedEmergency.priority)}
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" color="text.secondary">Estado</Typography>
                <Chip
                  label={selectedEmergency.status}
                  color={getStatusColor(selectedEmergency.status)}
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" color="text.secondary">Ubicación</Typography>
                <Typography variant="body1" mb={2}>
                  {selectedEmergency.location.address || 'Sin dirección'}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary">Descripción</Typography>
                <Typography variant="body1" mb={2}>
                  {selectedEmergency.description || 'Sin descripción'}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary">Creada</Typography>
                <Typography variant="body1">
                  {format(new Date(selectedEmergency.createdAt), 'dd/MM/yyyy HH:mm', { locale: es })}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>Cerrar</Button>
              {selectedEmergency.status === 'active' && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    resolveMutation.mutate(selectedEmergency.id);
                    setDetailsOpen(false);
                  }}
                >
                  Marcar como resuelta
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default EmergencyAlerts;
